import { useEffect, useMemo, useState } from "react";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { useCart } from "@/context/CartContext";
import { api, INR, formatApiError } from "@/lib/api";
import { Minus, Plus, Trash2, X, MessageCircle, Truck, Tag, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";

export default function CartDrawer() {
  const { items, open, setOpen, updateQty, removeItem, totals, clear } = useCart();
  const [form, setForm] = useState({ name: "", phone: "", address: "" });
  const [submitting, setSubmitting] = useState(false);
  const [settings, setSettings] = useState(null);
  const [promoInput, setPromoInput] = useState("");
  const [promo, setPromo] = useState(null); // {code, percent}
  const [promoChecking, setPromoChecking] = useState(false);

  useEffect(() => {
    if (!open || settings) return;
    (async () => {
      try {
        const { data } = await api.get("/settings");
        setSettings(data);
      } catch { /* noop */ }
    })();
  }, [open, settings]);

  // Auto-remove promo if cart drops below the minimum threshold
  useEffect(() => {
    if (!promo || !settings) return;
    const minSub = settings.promo_min_subtotal ?? 0;
    if (totals.amount < minSub) {
      setPromo(null);
      toast.info(`Promo removed — order is below the ₹${minSub} minimum`);
    }
  }, [promo, settings, totals.amount]);

  const promoEligible = useMemo(() => {
    if (!settings?.promo_active) return false;
    return totals.amount >= (settings.promo_min_subtotal ?? 0);
  }, [settings, totals.amount]);

  const discount = useMemo(() => {
    if (!promo) return 0;
    return Math.round((totals.amount * promo.percent) / 100);
  }, [promo, totals.amount]);

  const subtotalAfterDiscount = Math.max(0, totals.amount - discount);

  const shippingInfo = useMemo(() => {
    if (!settings || items.length === 0) return { amount: 0, label: "—" };
    const { shipping_standard, shipping_reduced, shipping_threshold_reduced, shipping_threshold_free } = settings;
    if (subtotalAfterDiscount >= shipping_threshold_free) return { amount: 0, label: "FREE" };
    if (subtotalAfterDiscount >= shipping_threshold_reduced) return { amount: shipping_reduced, label: INR(shipping_reduced) };
    return { amount: shipping_standard, label: INR(shipping_standard) };
  }, [settings, subtotalAfterDiscount, items.length]);

  const grandTotal = subtotalAfterDiscount + shippingInfo.amount;

  const nextFreeHint = useMemo(() => {
    if (!settings || items.length === 0) return null;
    const { shipping_threshold_reduced, shipping_threshold_free } = settings;
    if (subtotalAfterDiscount >= shipping_threshold_free) return "You've unlocked free shipping 🎉";
    if (subtotalAfterDiscount >= shipping_threshold_reduced) {
      const diff = shipping_threshold_free - subtotalAfterDiscount;
      return `Add ${INR(diff)} more for FREE shipping.`;
    }
    const diff = shipping_threshold_reduced - subtotalAfterDiscount;
    return `Add ${INR(diff)} more for ₹50 shipping.`;
  }, [settings, subtotalAfterDiscount, items.length]);

  const applyPromo = async () => {
    const code = promoInput.trim();
    if (!code) return;
    setPromoChecking(true);
    try {
      const { data } = await api.post("/promo/validate", { code });
      setPromo({ code: data.code, percent: data.percent });
      toast.success(`${data.percent}% off applied!`);
    } catch (err) {
      setPromo(null);
      toast.error(formatApiError(err.response?.data?.detail) || "Invalid code");
    } finally {
      setPromoChecking(false);
    }
  };

  const removePromo = () => {
    setPromo(null);
    setPromoInput("");
  };

  const buildMessage = () => {
    const lines = [];
    lines.push("*NEW ORDER — tpg.decants*");
    lines.push("");
    lines.push("*Customer Details*");
    lines.push(`Name: ${form.name}`);
    lines.push(`Phone: ${form.phone}`);
    lines.push(`Address: ${form.address}`);
    lines.push("");
    lines.push("*Order Items*");
    items.forEach((i, idx) => {
      lines.push(`${idx + 1}. ${i.brand} — ${i.name} (${i.size}) × ${i.qty} = ${INR(i.price * i.qty)}`);
    });
    lines.push("");
    lines.push(`Subtotal: ${INR(totals.amount)}`);
    if (promo) lines.push(`Promo (${promo.code}): -${INR(discount)} (${promo.percent}%)`);
    lines.push(`Shipping: ${shippingInfo.amount === 0 ? "FREE" : INR(shippingInfo.amount)}`);
    lines.push(`*Grand Total: ${INR(grandTotal)}*`);
    lines.push("");
    lines.push("*Payment*");
    lines.push(`Please pay ${INR(grandTotal)} via UPI to:`);
    lines.push(`*${settings?.upi_id || "sak.2@ybl"}*`);
    lines.push("(GPay / PhonePe / any UPI app)");
    lines.push("");
    lines.push("📸 *Kindly share the payment screenshot here to confirm your order.*");
    return encodeURIComponent(lines.join("\n"));
  };

  const placeOrder = async () => {
    if (!form.name.trim() || !form.phone.trim() || !form.address.trim()) {
      toast.error("Please fill name, phone, and address");
      return;
    }
    if (items.length === 0) {
      toast.error("Cart is empty");
      return;
    }

    setSubmitting(true);
    try {
      let s = settings;
      if (!s) {
        const { data } = await api.get("/settings");
        s = data;
        setSettings(s);
      }
      try {
        await api.post("/orders", {
          items: items.map((i) => ({ product_id: i.product_id, size: i.size, qty: i.qty })),
          customer_name: form.name,
          customer_phone: form.phone,
          customer_address: form.address,
        });
      } catch (err) {
        const detail = err.response?.data?.detail;
        toast.error(typeof detail === "string" ? detail : "Could not place order");
        setSubmitting(false);
        return;
      }

      const phone = (s.whatsapp_number || "").replace(/[^0-9]/g, "");
      const msg = buildMessage();
      // Device-specific WhatsApp URL:
      //  • Mobile → wa.me deep-links straight into the WhatsApp app.
      //  • Desktop → web.whatsapp.com opens the chat inside WhatsApp Web
      //    directly, skipping the localized interstitial page entirely.
      const isMobile = /iPhone|iPad|iPod|Android|Mobile/i.test(
        typeof navigator !== "undefined" ? navigator.userAgent : ""
      );
      const url = isMobile
        ? `https://wa.me/${phone}?text=${msg}`
        : `https://web.whatsapp.com/send?phone=${phone}&text=${msg}`;

      // Clear cart BEFORE navigating so state is fresh when they return.
      clear();
      setForm({ name: "", phone: "", address: "" });
      setPromo(null);
      setPromoInput("");
      setOpen(false);

      // Navigate the current tab — bypasses all pop-up blockers.
      window.location.href = url;
    } catch {
      toast.error("Could not start checkout. Try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Sheet open={open} onOpenChange={setOpen}>
      <SheetContent
        side="right"
        data-testid="cart-drawer"
        className="w-full sm:max-w-md bg-[var(--bg-surface)] border-l border-[var(--border-default)] text-[var(--text-primary)] p-0 flex flex-col"
      >
        <SheetHeader className="px-6 pt-6 pb-4 border-b border-[var(--border-default)]">
          <SheetTitle className="font-headings text-2xl text-[var(--text-primary)] flex items-center justify-between">
            <span>Your Cart</span>
            <span className="text-sm text-[var(--text-muted)] font-body tracking-wider">
              {totals.count} item{totals.count !== 1 ? "s" : ""}
            </span>
          </SheetTitle>
        </SheetHeader>

        <div className="flex-1 overflow-y-auto px-6 py-4 space-y-4">
          {items.length === 0 ? (
            <div data-testid="cart-empty" className="text-center text-[var(--text-secondary)] py-16">
              Your cart is empty.
              <br />
              <span className="text-xs text-[var(--text-muted)]">Add some decants to get started.</span>
            </div>
          ) : (
            items.map((i) => (
              <div key={i.key} data-testid={`cart-item-${i.key}`} className="flex gap-3 border-b border-[var(--border-default)] pb-4">
                <div className="flex-1">
                  <div className="text-[10px] uppercase tracking-[0.2em] text-[var(--text-muted)]">{i.brand}</div>
                  <div className="font-headings text-base text-[var(--text-primary)] leading-snug">{i.name}</div>
                  <div className="text-xs text-[var(--text-secondary)] mt-1">Size: {i.size} · {INR(i.price)}</div>
                  <div className="mt-2 flex items-center gap-2">
                    <button data-testid={`cart-dec-${i.key}`} onClick={() => updateQty(i.key, i.qty - 1)} className="p-1 border border-[var(--border-default)] hover:border-[var(--brand-gold)] text-[var(--text-secondary)]">
                      <Minus size={14} />
                    </button>
                    <span className="w-6 text-center text-sm">{i.qty}</span>
                    <button data-testid={`cart-inc-${i.key}`} onClick={() => updateQty(i.key, i.qty + 1)} className="p-1 border border-[var(--border-default)] hover:border-[var(--brand-gold)] text-[var(--text-secondary)]">
                      <Plus size={14} />
                    </button>
                    <button data-testid={`cart-remove-${i.key}`} onClick={() => removeItem(i.key)} className="ml-auto p-1 text-[var(--text-muted)] hover:text-red-400">
                      <Trash2 size={14} />
                    </button>
                  </div>
                </div>
                <div className="font-headings text-[var(--brand-gold)]">{INR(i.price * i.qty)}</div>
              </div>
            ))
          )}
        </div>

        {items.length > 0 && (
          <div className="border-t border-[var(--border-default)] px-6 py-5 space-y-3 bg-[var(--bg-default)]">
            <div className="space-y-2">
              <input data-testid="checkout-name" type="text" placeholder="Your name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="w-full bg-transparent border border-[var(--border-default)] focus:border-[var(--brand-gold)] focus:outline-none px-3 py-2 text-sm" />
              <input data-testid="checkout-phone" type="tel" placeholder="Phone number (for delivery)" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="w-full bg-transparent border border-[var(--border-default)] focus:border-[var(--brand-gold)] focus:outline-none px-3 py-2 text-sm" />
              <textarea data-testid="checkout-address" placeholder="Shipping address" rows={2} value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} className="w-full bg-transparent border border-[var(--border-default)] focus:border-[var(--brand-gold)] focus:outline-none px-3 py-2 text-sm resize-none" />
            </div>

            {/* Promo code — shown only if active AND cart meets min */}
            {settings?.promo_active && (
              <div>
                {!promoEligible ? (
                  <div
                    data-testid="promo-min-hint"
                    className="flex items-center gap-2 px-3 py-2 border border-[var(--border-default)]/60 text-xs text-[var(--text-muted)]"
                  >
                    <Tag size={13} className="text-[var(--accent-gold)]/70" />
                    Add {INR((settings.promo_min_subtotal ?? 0) - totals.amount)} more to use a promo code.
                  </div>
                ) : promo ? (
                  <div
                    data-testid="promo-applied"
                    className="flex items-center justify-between bg-[var(--brand-gold)]/10 border border-[var(--brand-gold)]/40 px-3 py-2 text-sm"
                  >
                    <span className="flex items-center gap-2 text-[var(--brand-gold)]">
                      <CheckCircle2 size={14} />
                      <span className="font-medium">{promo.code}</span>
                      <span className="text-[var(--text-secondary)]">— {promo.percent}% off</span>
                    </span>
                    <button
                      data-testid="promo-remove"
                      onClick={removePromo}
                      className="text-[var(--text-muted)] hover:text-red-400"
                      aria-label="Remove promo"
                    >
                      <X size={14} />
                    </button>
                  </div>
                ) : (
                  <div className="flex gap-2">
                    <div className="flex-1 relative">
                      <Tag size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--text-muted)]" />
                      <input
                        data-testid="promo-input"
                        type="text"
                        placeholder="Promo code"
                        value={promoInput}
                        onChange={(e) => setPromoInput(e.target.value.toUpperCase())}
                        onKeyDown={(e) => e.key === "Enter" && applyPromo()}
                        className="w-full bg-transparent border border-[var(--border-default)] focus:border-[var(--brand-gold)] focus:outline-none pl-8 pr-3 py-2 text-sm uppercase tracking-wider"
                      />
                    </div>
                    <button
                      data-testid="promo-apply"
                      onClick={applyPromo}
                      disabled={promoChecking || !promoInput.trim()}
                      className="px-4 py-2 border border-[var(--border-default)] text-xs uppercase tracking-[0.2em] text-[var(--text-primary)] hover:border-[var(--brand-gold)] hover:text-[var(--brand-gold)] disabled:opacity-60"
                    >
                      {promoChecking ? "…" : "Apply"}
                    </button>
                  </div>
                )}
              </div>
            )}

            {/* Totals */}
            <div className="pt-2 space-y-1.5 text-sm">
              <div className="flex justify-between text-[var(--text-secondary)]">
                <span>Subtotal</span>
                <span>{INR(totals.amount)}</span>
              </div>
              {promo && discount > 0 && (
                <div data-testid="discount-line" className="flex justify-between text-[var(--brand-gold)]">
                  <span>Promo ({promo.code} · {promo.percent}%)</span>
                  <span>−{INR(discount)}</span>
                </div>
              )}
              <div className="flex justify-between text-[var(--text-secondary)]">
                <span className="flex items-center gap-1.5"><Truck size={13} /> Shipping</span>
                <span className={shippingInfo.amount === 0 ? "text-[var(--brand-gold)] font-medium" : ""}>{shippingInfo.label}</span>
              </div>
              {nextFreeHint && (
                <div className="text-[11px] text-[var(--accent-gold)]/80 italic pt-0.5">{nextFreeHint}</div>
              )}
              <div className="flex justify-between items-center pt-2 border-t border-[var(--border-default)]">
                <span className="text-xs uppercase tracking-[0.25em] text-[var(--text-muted)]">Total</span>
                <span data-testid="cart-grand-total" className="font-headings text-2xl text-[var(--brand-gold)]">{INR(grandTotal)}</span>
              </div>
            </div>

            {settings?.upi_id && (
              <div className="text-[11px] text-[var(--text-muted)] leading-relaxed">
                Pay via UPI to <span className="text-[var(--brand-gold)] font-medium">{settings.upi_id}</span> (GPay / PhonePe).
                Share the payment screenshot on WhatsApp to confirm your order.
              </div>
            )}

            <button
              data-testid="checkout-whatsapp"
              disabled={submitting}
              onClick={placeOrder}
              className="w-full flex items-center justify-center gap-2 py-3 bg-[#25D366] text-black font-medium tracking-wide hover:bg-[#20c05d] transition-colors disabled:opacity-60"
            >
              <MessageCircle size={18} />
              {submitting ? "Opening WhatsApp…" : "Place Order via WhatsApp"}
            </button>
            <button data-testid="cart-clear" onClick={clear} className="w-full flex items-center justify-center gap-1 py-2 text-xs uppercase tracking-[0.2em] text-[var(--text-muted)] hover:text-red-400">
              <X size={14} />
              Clear cart
            </button>
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
}
